import React, { memo, useCallback, useEffect, useRef, useState } from "react";

import {
  Badge,
  GenerateButton,
  ItemCard,
  LoadingSpinnerBlur,
  ModelName,
  RunningText,
  StyledImage,
  StyledVideo,
  Username,
  VideoFooter,
} from "./ExploreItem.styles";
import { COLLECTION_STATUS, MEDIA_TYPE } from "../../utils/constants";
import Loader from "../utils/Loader/Loader";
import { ThumbnailImageSmall } from "../VideoItem/VideoItem.styles";
import { useInView } from "react-intersection-observer";
import { useSelector } from "react-redux";
import { selectAutoPlay } from "../../store/slices/common/slice";
import { formatModelName } from "../../utils/utils";
import { useMediaQuery } from "react-responsive";

const ExploreItem = ({ item, onItemClick }) => {
  const [isHovered, setIsHovered] = useState(false);
  const autoplayEnabled = useSelector(selectAutoPlay);
  const videoRef = useRef();

  const [inputUrl, setInputUrl] = useState(null);
  const [outputUrl, setOutputUrl] = useState(null);

  const { ref: containerRef, inView } = useInView({
    threshold: 0.01,
    rootMargin: "300px",
    triggerOnce: true,
  });

  const { ref: autoplayRef, inView: isInAutoplayView } = useInView({
    threshold: 0.7,
    rootMargin: "0px",
  });

  useEffect(() => {
    if (!inView) return;
    setInputUrl(
      (prev) =>
        prev || item?.input?.image?.resizedImage?.url || item?.input?.image?.url
    );
    setOutputUrl(
      (prev) =>
        prev ||
        (item.output.type === MEDIA_TYPE.IMAGE
          ? item?.output?.image?.url
          : item?.output?.video?.compressedUrl || item?.output?.video?.url)
    );
  }, [inView, item]);

  useEffect(() => {
    if (
      !videoRef.current ||
      !outputUrl ||
      item.output.type !== MEDIA_TYPE.VIDEO
    )
      return;

    const video = videoRef.current;
    if (video.src !== outputUrl) {
      video.src = outputUrl;
      video.load();
    }
  }, [outputUrl, item.output.type]);

  const inProgress =
    item.status === COLLECTION_STATUS.IN_PROGRESS ||
    item.status === COLLECTION_STATUS.NUDIFY_IN_PROGRESS;

  const handleVideoHover = useCallback(() => {
    if (videoRef.current && videoRef.current.readyState >= 2) {
      videoRef.current.play().catch(() => {});
    }
  }, []);

  const handleVideoLeave = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
  }, []);

  const ensureInlinePlayback = useCallback(() => {
    const element = videoRef.current;
    if (!element) return;

    element.muted = true;
    element.defaultMuted = true;
    element.playsInline = true;
    element.loop = true;
    element.preload = "none";
    element.setAttribute("muted", "");
    element.setAttribute("playsinline", "");
    element.setAttribute("webkit-playsinline", "");
    element.setAttribute("loop", "");
  }, []);

  useEffect(() => {
    if (!videoRef.current) return;

    ensureInlinePlayback();

    const shouldPlay = autoplayEnabled && isInAutoplayView;

    if (shouldPlay) {
      const playPromise = videoRef.current.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch((error) => {
          console.log(error);
        });
      }
    } else {
      videoRef.current.pause();
      //   videoRef.current.currentTime = 0;
    }
  }, [ensureInlinePlayback, autoplayEnabled, isInAutoplayView, outputUrl]);

  const isMobile = useMediaQuery({ maxWidth: 768 });

  const touchStartY = useRef(0);
  const touchStartTime = useRef(0);

  const handleTouchStart = (e) => {
    touchStartY.current = e.touches[0].clientY;
    touchStartTime.current = Date.now();
    setIsHovered(true);
  };

  const handleTouchEnd = (e) => {
    setIsHovered(false);
    const diffY = Math.abs(e.changedTouches[0].clientY - touchStartY.current);
    const pressDuration = Date.now() - touchStartTime.current;

    // only count as tap if short (<500 ms) and not a scroll
    if (diffY < 10 && pressDuration < 500) {
      setTimeout(() => {
        onItemClick?.();
      }, 200);
    }
  };

  return (
    <ItemCard
      id={item._id}
      ref={(el) => {
        containerRef(el);
        autoplayRef(el);
      }}
      onContextMenu={(e) => {
        e.preventDefault();
      }}
      onClick={!isMobile ? onItemClick : undefined}
      style={{
        aspectRatio:
          item?.input?.image?.resizedImage?.width /
          item?.input?.image?.resizedImage?.height,
      }}
      onMouseEnter={() => {
        if (!isMobile) {
          setIsHovered(true);
          if (!autoplayEnabled) {
            handleVideoHover();
          }
        }
      }}
      onMouseLeave={() => {
        if (!isMobile) {
          setIsHovered(false);
          if (!autoplayEnabled) {
            handleVideoLeave();
          }
        }
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <ThumbnailImageSmall src={inputUrl} alt={item?.title} className="small" />
      {item.output.type === MEDIA_TYPE.IMAGE ? (
        <StyledImage src={outputUrl} />
      ) : (
        <StyledVideo ref={videoRef} src={outputUrl} imageSrc={inputUrl} />
      )}

      {inProgress && (
        <LoadingSpinnerBlur $show>
          <RunningText>
            <Loader size={32} color="white" />
            <div className="running-title">RUNNING</div>
            <div className="running-message">
              It may take 3-5 minutes to generate the video due to the
              <br />
              computational cost.
            </div>
          </RunningText>
        </LoadingSpinnerBlur>
      )}

      <GenerateButton className="generate-button" $show={isHovered}>
        Generate
      </GenerateButton>

      <VideoFooter className="video-footer" $hideContent={isHovered}>
        <ModelName>{formatModelName(item.name)}</ModelName>
        {item.username && <Username>@{item.username}</Username>}
        {item?.tags && item?.tags?.length > 0 && (
          <Badge $variant={item?.tags[0]?.name}>{item?.tags[0]?.name}</Badge>
        )}
      </VideoFooter>
    </ItemCard>
  );
};

export default memo(ExploreItem);
